import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  PoolAttributes,
  SourcingPurchaseBulkCreateRequest,
  SourcingService,
} from 'api-clients/monolith';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useToaster } from '@/components/base/toaster';
import { useBudgetType } from '@/hooks/use-budget-type';
import { POOL } from '@/types/channels';

import { JobPostingForm } from './types';
import { useValidationSchema } from './validation/use-validation-schema';
interface UseJobPostingFormProps {
  clearJobPostingStorage: () => void;
}

export function useJobPostingForm({
  clearJobPostingStorage,
}: UseJobPostingFormProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifySuccess, notifyError } = useToaster();
  const validationSchema = useValidationSchema();
  const { calculateTotalBudget } = useBudgetType();
  const hookForm = useForm<JobPostingForm>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { getValues, handleSubmit } = hookForm;

  /* Post job by calling the bulk create API */
  const { mutate: postJob, isPending: isSubmitting } = useMutation({
    mutationFn: (requestBody: SourcingPurchaseBulkCreateRequest) =>
      SourcingService.postInternalApiSourcingSourcingPurchasesBulkCreate(
        requestBody,
      ),
    onSuccess: data => {
      const { total, created, failed } = data.summary;
      if (created === total && failed === 0) {
        notifySuccess({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{total, plural, one {Job posting has been created successfully} other {All {total} job postings have been created successfully}}',
              description: 'Success message for successful job posting',
            },
            { total },
          ),
        });
      } else {
        notifyError({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{total, plural, one {Failed to create job posting} other {Among {total} jobs, {created} jobs were posted successfully and {failed} jobs failed to be posted}}',
              description: 'Error message for job posting creation',
            },
            { total, created, failed },
          ),
        });
      }

      clearJobPostingStorage();

      const dashboardPath = location.pathname.replace(
        '/post-job',
        '/dashboard',
      );
      navigate(dashboardPath); // TODO: Navigating to dashboard instead of success page for now.
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to create job posting',
          description: 'Error message for job posting creation',
        }),
      });
    },
  });

  const getPoolFormData = (openingId: string): PoolAttributes => {
    const pool = getValues('pool');
    return {
      campaign_type: pool.campaignType,
      from_email: pool.fromEmail,
      is_never_ending: pool.isNeverEnding,
      audience_id: pool.openingMessages[openingId].audienceId,
      subject: pool.openingMessages[openingId].subject,
      body: pool.openingMessages[openingId].body,
    };
  };

  const handlePostJob = handleSubmit(() => {
    const formData = getValues();
    const poolFormData = formData.pool;

    const sourcingPurchases = Object.entries(formData.channels).flatMap(
      ([channelSlug, channelData]) => {
        return Object.entries(channelData.openingBudgets)
          .filter(([_, openingBudget]) => openingBudget !== null)
          .map(([openingId, openingBudget]) => ({
            funnel_external_id: openingId,
            sourcing_channel_slug: channelSlug,
            budget: openingBudget ?? 0, // We have budget validation in the form, so we can safely assume it's not null
            total_budget: calculateTotalBudget({
              budgetType: channelData.budgetType,
              budgetPerUnit: openingBudget ?? 0,
              startDate: channelData.startsAt,
              endDate: channelData.endAt,
            }),
            starts_at: channelData.startsAt,
            end_at: channelData.endAt,
            posting_title: formData.jobDetails[openingId].title,
            budget_type: channelData.budgetType,
            end_at_type: channelData.endAtType,
            pool_attributes: null,
          }));
      },
    );

    const poolSourcingPurchases = Object.entries(
      poolFormData.openingMessages,
    ).map(([openingId]) => ({
      funnel_external_id: openingId,
      sourcing_channel_slug: POOL,
      budget: 0,
      total_budget: 0,
      posting_title: formData.jobDetails[openingId].title,
      budget_type: null,
      end_at_type: null,
      starts_at: poolFormData.startsAt,
      end_at: poolFormData.endAt,
      pool_attributes: getPoolFormData(openingId),
    }));

    const requestBody: SourcingPurchaseBulkCreateRequest = {
      sourcing_purchases: [...sourcingPurchases, ...poolSourcingPurchases],
    };

    postJob(requestBody);
  });

  return { hookForm, isSubmitting, handlePostJob };
}
